import React, { useState, useEffect } from 'react';
import { 
  Text, 
  TextInput, 
  Group, 
  ActionIcon,
  Box
} from '@mantine/core';
import { IconPencil, IconDeviceFloppy, IconX } from '@tabler/icons-react';
import { ImageDetails } from '../../../../../types/Image';

interface FilenameSectionProps {
  filename: string;
  onSaveFileName: (newFileName: string) => void;
}

export const FilenameSection: React.FC<FilenameSectionProps> = ({ 
  filename, 
  onSaveFileName
}) => {
  const [editing, setEditing] = useState(false);
  const [editedName, setEditedName] = useState(filename);
  
  // Extract file extension from the original filename
  const getFileNameAndExtension = (fullName: string): [string, string] => {
    const lastDotIndex = fullName.lastIndexOf('.');
    if (lastDotIndex === -1) return [fullName, ''];
    
    const name = fullName.substring(0, lastDotIndex);
    const extension = fullName.substring(lastDotIndex);
    return [name, extension];
  };
  
  // Update edited name when filename prop changes
  useEffect(() => {
    setEditedName(filename);
  }, [filename]);
  
  // Handle save action
  const handleSaveFileName = () => {
    onSaveFileName(editedName);
    setEditing(false);
  };
  
  // Handle cancel action
  const handleCancel = () => {
    setEditedName(filename);
    setEditing(false);
  };
  
  // Split filename and extension for editing
  const [baseName, extension] = getFileNameAndExtension(editedName);
  
  return (
    editing ? 
      <Group gap="5px" justify="space-between" style={{flex: 1}}>
        <TextInput
          size="sm"
          placeholder="Enter file name"
          value={baseName}
          onChange={(e) => setEditedName(e.target.value + extension)}
          style={{ flex: 1 }}
        />
        {extension && (
          <Text size="sm" style={{ alignSelf: 'center' }}>
            {extension}
          </Text>
        )}
        <ActionIcon ml="5px" size="lg" variant="filled" color="blue" onClick={handleSaveFileName}>
          <IconDeviceFloppy size={16}/>
        </ActionIcon>
      </Group>
    :
      <Group justify={'space-between'} style={{ width: '100%' }} wrap="nowrap">
        <Text 
          size="sm" 
          truncate="end" 
          style={{ 
            maxWidth: 'calc(100% - 40px)', /* Subtract the width of the edit button */
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          title={editedName || 'Unnamed'} /* Show full filename on hover */
        >
          {editedName || 'Unnamed'}
        </Text>
        <ActionIcon size="lg" variant="subtle" onClick={() => setEditing(true)} style={{ flexShrink: 0 }}>
          <IconPencil size={16}/>
        </ActionIcon>
      </Group>
  );
};
